import { Helmet } from "react-helmet-async";

const SITE_URL = "https://tripsi.ca";
const DEFAULT_OG_IMAGE = "https://customer-assets.emergentagent.com/job_tripsi-launch/artifacts/7y1dc8ot_full_logo_red.png";

export function SEOHead({ title, description, canonical, ogTitle, ogDescription, ogImage, ogType = "website", noindex = false, jsonLd }) {
  const fullTitle = title ? `${title} | Tripsi` : "Tripsi | Affordable Rideshare & Community Transportation in Canada";
  const desc = description || "Affordable rides, professional drivers, business transportation and community-focused mobility. Book through the Tripsi app in Saskatchewan or call dispatch in Ontario.";
  const canonicalUrl = canonical ? `${SITE_URL}${canonical}` : SITE_URL;
  const ogT = ogTitle || fullTitle;
  const ogD = ogDescription || desc;
  const ogImg = ogImage || DEFAULT_OG_IMAGE;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={canonicalUrl} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={ogT} />
      <meta property="og:description" content={ogD} />
      <meta property="og:image" content={ogImg} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content="Tripsi" />
      <meta property="og:locale" content="en_CA" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={ogT} />
      <meta name="twitter:description" content={ogD} />
      <meta name="twitter:image" content={ogImg} />

      {/* Additional */}
      <meta name="theme-color" content="#E60000" />
      <meta name="application-name" content="Tripsi" />

      {/* hreflang for bilingual support */}
      <link rel="alternate" hrefLang="en" href={canonicalUrl} />
      <link rel="alternate" hrefLang="fr" href={canonicalUrl} />
      <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />

      {jsonLd && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}
    </Helmet>
  );
}

// ── JSON-LD Schemas ──

export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Tripsi",
  "url": "https://tripsi.ca",
  "logo": DEFAULT_OG_IMAGE,
  "description": "Affordable rideshare and community transportation across Canada.",
  "foundingCountry": "CA",
  "contactPoint": [
    {
      "@type": "ContactPoint",
      "telephone": "+1-647-249-9977",
      "contactType": "customer service",
      "areaServed": ["CA"],
      "availableLanguage": ["English", "French"]
    }
  ],
  "sameAs": []
};

export const taxiServiceSchema = {
  "@context": "https://schema.org",
  "@type": "TaxiService",
  "name": "Tripsi",
  "description": "Affordable rideshare and transportation service across Canada. Book via app in Saskatchewan or call dispatch for Ontario.",
  "url": "https://tripsi.ca",
  "telephone": "+1-647-249-9977",
  "areaServed": [
    { "@type": "Province", "name": "Saskatchewan", "containedInPlace": { "@type": "Country", "name": "Canada" } },
    { "@type": "Province", "name": "Ontario", "containedInPlace": { "@type": "Country", "name": "Canada" } }
  ],
  "provider": {
    "@type": "Organization",
    "name": "Tripsi",
    "url": "https://tripsi.ca"
  },
  "offers": {
    "@type": "Offer",
    "description": "Affordable community rides",
    "areaServed": "Canada"
  }
};

export const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "Tripsi",
  "url": "https://tripsi.ca",
  "potentialAction": {
    "@type": "SearchAction",
    "target": "https://tripsi.ca/?q={search_term_string}",
    "query-input": "required name=search_term_string"
  }
};

export const mobileAppSchema = {
  "@context": "https://schema.org",
  "@type": "MobileApplication",
  "name": "Tripsi",
  "operatingSystem": "iOS, Android",
  "applicationCategory": "TravelApplication",
  "description": "Book affordable rides across Canada. Available in Saskatchewan via app, Ontario via dispatch.",
  "offers": { "@type": "Offer", "price": "0", "priceCurrency": "CAD" }
};

export function buildFAQSchema(faqs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(f => ({
      "@type": "Question",
      "name": f.question,
      "acceptedAnswer": { "@type": "Answer", "text": f.answer }
    }))
  };
}

export function buildBreadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "name": item.name,
      "item": `${SITE_URL}${item.url}`
    }))
  };
}
